import { MUICheckBox, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { MouseEvent, useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { Navigate } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../../components/DiMaDetailsContent"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToFeature,
} from "../../../../../models/Authorization"
import { DeviceRoleGet, DeviceRolePut } from "../../../../../models/DeviceRole"
import { UserInfo } from "../../../../../models/User"

type DeviceRoleBooleanFields = "isDefault" | "isAdmin" | "isOwner" | "isAssignable"

const validationSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  description: z.string(),
  isDefault: z.boolean({ error: "Is Default is required" }),
  isAdmin: z.boolean({ error: "Is Admin is required" }),
  isOwner: z.boolean({ error: "Is Owner is required" }),
  isAssignable: z.boolean({ error: "Is Assignable is required" }),
  maxOccurrences: z.number({ error: "Max Occurrences is required" }),
})

export function DeviceRoleDetails(props: {
  readonly role: DeviceRoleGet
  readonly deviceId: string
  readonly user: UserInfo
  readonly setRole: (updatedRole: DeviceRoleGet) => void
}) {
  const { role, deviceId, user, setRole } = props

  const returnURL = `/devices/${deviceId}#roles`

  const formMethods = useForm<DeviceRolePut>({
    resolver: zodResolver(validationSchema),
  })

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({
      name: role.name,
      description: role.description,
      isDefault: role.isDefault || false,
      isAdmin: role.isAdmin || false,
      isOwner: role.isOwner || false,
      isAssignable: role.isAssignable || false,
      maxOccurrences: role.maxOccurrences || 0,
    })
  }, [role, reset])

  const onSubmit = useCallback(
    (values: DeviceRolePut) =>
      digitalManagerApi
        .put<DeviceRoleGet>(`/devices/${deviceId}/roles/${role.id}`, {
          name: values.name,
          description: values.description,
          isDefault: values.isDefault,
          isAdmin: values.isAdmin,
          isOwner: values.isOwner,
          isAssignable: values.isAssignable,
          maxOccurrences: values.maxOccurrences,
        })
        .then((res) => {
          toast.success("Role updated")
          setRole(res.data)
        })
        .catch(() => {
          toast.error("Cannot update role")
          throw new Error("Cannot update role")
        }),
    [deviceId, role.id, setRole]
  )

  const handleTextFieldChange = useCallback(
    (field: ControllerRenderProps<DeviceRolePut, "name" | "description">) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const handleBooleanToggle = useCallback(
    (field: ControllerRenderProps<DeviceRolePut, DeviceRoleBooleanFields>) =>
      (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        field.onChange(!field.value)
      },
    []
  )

  const renderNameTextField = useCallback(
    ({ field }: { field: ControllerRenderProps<DeviceRolePut, "name"> }) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name, handleTextFieldChange]
  )

  const renderDescriptionTextField = useCallback(
    ({ field }: { field: ControllerRenderProps<DeviceRolePut, "description"> }) => (
      <MUITextField
        id="description"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.description?.message === "string"
            ? { description: { message: errors.description.message } }
            : undefined
        }
      />
    ),
    [errors.description, handleTextFieldChange]
  )

  const renderIsDefaultCheckbox = useCallback(
    ({ field }: { field: ControllerRenderProps<DeviceRolePut, "isDefault"> }) => (
      <MUICheckBox
        {...field}
        id="isDefault"
        checked={field.value}
        handleToggle={handleBooleanToggle(field)}
      />
    ),
    [handleBooleanToggle]
  )

  const renderIsAdminCheckbox = useCallback(
    ({ field }: { field: ControllerRenderProps<DeviceRolePut, "isAdmin"> }) => (
      <MUICheckBox
        {...field}
        id="isAdmin"
        checked={field.value}
        handleToggle={handleBooleanToggle(field)}
      />
    ),
    [handleBooleanToggle]
  )

  const renderIsOwnerCheckbox = useCallback(
    ({ field }: { field: ControllerRenderProps<DeviceRolePut, "isOwner"> }) => (
      <MUICheckBox
        {...field}
        id="isOwner"
        checked={field.value}
        handleToggle={handleBooleanToggle(field)}
      />
    ),
    [handleBooleanToggle]
  )

  const renderIsAssignableCheckbox = useCallback(
    ({ field }: { field: ControllerRenderProps<DeviceRolePut, "isAssignable"> }) => (
      <MUICheckBox
        {...field}
        id="isAssignable"
        checked={field.value}
        handleToggle={handleBooleanToggle(field)}
      />
    ),
    [handleBooleanToggle]
  )

  const handleNumberInputChange = useCallback(
    (field: ControllerRenderProps<DeviceRolePut, "maxOccurrences">) =>
      (value: string) => {
        const parsed = value === "" ? Number.NaN : Number(value)
        field.onChange(parsed)
      },
    []
  )

  const renderMaxOccurrencesTextField = useCallback(
    ({ field }: { field: ControllerRenderProps<DeviceRolePut, "maxOccurrences"> }) => (
      <MUITextField
        id="maxOccurrences"
        type="number"
        size={32}
        name={field.name}
        value={
          field.value !== undefined && field.value !== null && !Number.isNaN(field.value)
            ? String(field.value)
            : ""
        }
        onChange={handleNumberInputChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.maxOccurrences?.message === "string"
            ? { maxOccurrences: { message: errors.maxOccurrences.message } }
            : undefined
        }
      />
    ),
    [errors.maxOccurrences, handleNumberInputChange]
  )

  if (!role) {
    return <Navigate to={returnURL} />
  }

  const detailsContentProps: DiMaDetailsContentProps<DeviceRolePut> = {
    formMethods,
    canWrite: hasUserAccessToFeature(
      user.authorizations,
      DimaFeatureShortNameEnum.DM,
      AccessLevel.Write
    ),
    inlineEdit: true,
    listItems: [
      {
        id: 0,
        label: "Name",
        displayItem: <div style={{ width: "100%" }}>{role.name}</div>,
        editItem: (
          <Controller name="name" control={control} render={renderNameTextField} />
        ),
      },
      {
        id: 1,
        label: "Description",
        displayItem: <div style={{ width: "100%" }}>{role.description}</div>,
        editItem: (
          <Controller
            name="description"
            control={control}
            render={renderDescriptionTextField}
          />
        ),
      },
      {
        id: 2,
        label: "Is Default",
        displayItem: (
          <div style={{ width: "100%" }}>{role.isDefault?.toString() || "false"}</div>
        ),
        editItem: (
          <Controller
            name="isDefault"
            control={control}
            render={renderIsDefaultCheckbox}
          />
        ),
      },
      {
        id: 3,
        label: "Is Admin",
        displayItem: (
          <div style={{ width: "100%" }}>{role.isAdmin?.toString() || "false"}</div>
        ),
        editItem: (
          <Controller name="isAdmin" control={control} render={renderIsAdminCheckbox} />
        ),
      },
      {
        id: 4,
        label: "Is Owner",
        displayItem: (
          <div style={{ width: "100%" }}>{role.isOwner?.toString() || "false"}</div>
        ),
        editItem: (
          <Controller name="isOwner" control={control} render={renderIsOwnerCheckbox} />
        ),
      },
      {
        id: 5,
        label: "Is Assignable",
        displayItem: (
          <div style={{ width: "100%" }}>{role.isAssignable?.toString() || "false"}</div>
        ),
        editItem: (
          <Controller
            name="isAssignable"
            control={control}
            render={renderIsAssignableCheckbox}
          />
        ),
      },
      {
        id: 6,
        label: "Max Occurrences",
        displayItem: <div style={{ width: "100%" }}>{role.maxOccurrences}</div>,
        editItem: (
          <Controller
            name="maxOccurrences"
            control={control}
            render={renderMaxOccurrencesTextField}
          />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
